"use client";

import DeletePopover from "@/app/shared/delete-popover";
import { Form } from "@/components/ui/form";
import useApi from "@/hooks/useApi";
import { useTableData } from "@/hooks/useTableData";
import ControlledTable from "@/components/controlled-table";
import { HeaderCell } from "@/components/ui/table";
import SettingsRepository from "@/utilities/repositories/Settings";
import UserRepository from "@/utilities/repositories/Users";
import {
  TGoogleAdsAccount,
  TGoogleAdsAccountCreate,
} from "@/utilities/types/Setting";
import { IResponseData } from "@/utilities/types/requests";
import { IUserData } from "@/utilities/types/Users";
import {
  googleAdsAccountSchema,
  TGoogleAdsAccountForm,
} from "@/utils/validators/google-ads-account.schema";
import cn from "@/utils/class-names";
import { useCallback, useEffect, useState } from "react";
import { Controller, SubmitHandler, UseFormReturn } from "react-hook-form";
import toast from "react-hot-toast";
import { ActionIcon, Button, Checkbox, Input, Tab, Title, Tooltip } from "rizzui";
import {
  PiMagnifyingGlassBold,
  PiPencilSimpleBold,
  PiPlusBold,
} from "react-icons/pi";
import DataManagerLogsWrapper from "../integrations/DataManagerLogsWrapper";

const DEFAULT_VALUES: TGoogleAdsAccountForm = {
  id: "",
  name: "",
  accountId: "",
  conversionId: "",
  loginCustomerId: "",
  submitLeadFormConversionActionId: "",
  qualifiedLeadConversionActionId: "",
  notes: "",
};

function GoogleAdsAccountConfig() {
  const [formMethods, setFormMethods] =
    useState<UseFormReturn<TGoogleAdsAccountForm>>();
  const [reset, setReset] = useState<TGoogleAdsAccountForm>(DEFAULT_VALUES);
  const [selectedAccountId, setSelectedAccountId] = useState<string>();
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [isSavingUsers, setIsSavingUsers] = useState(false);
  const {
    request: getGoogleAdsAccounts,
    response: googleAdsAccountsResponse,
    loading: isLoadingAccounts,
  } = useApi<IResponseData<TGoogleAdsAccount[]>>({
    request: SettingsRepository.getGoogleAdsAccounts,
  });
  const { request: createGoogleAdsAccount, loading: isCreating } = useApi<
    IResponseData<TGoogleAdsAccount>
  >({
    request: SettingsRepository.createGoogleAdsAccount,
  });
  const { request: updateGoogleAdsAccount, loading: isUpdating } = useApi<
    IResponseData<TGoogleAdsAccount>
  >({
    request: SettingsRepository.updateGoogleAdsAccount,
  });
  const { request: deleteGoogleAdsAccount, loading: isDeleting } = useApi<
    IResponseData<void>
  >({
    request: SettingsRepository.deleteGoogleAdsAccount,
  });

  const googleAdsAccounts = googleAdsAccountsResponse?.data?.filter(Boolean) ?? [];
  const selectedAccount = googleAdsAccounts.find((account) => account.id === selectedAccountId);

  const {
    isLoading: isLoadingUsers,
    tableData: users,
    currentPage: userPage,
    totalItems: totalUsers,
    handlePaginate: paginateUsers,
    pageSize: userPageSize,
    handleChangePageSize: changeUserPageSize,
    searchTerm: userSearch,
    handleSearch: searchUsers,
    refresh: refreshUsers,
  } = useTableData<IUserData>({
    server: { request: UserRepository.getUsers },
    pagination: { page: 1, limit: 10 },
    searchBy: (term) => ({ search: term || undefined }),
  });

  const loadUsersForAccount = async (accountId: string) => {
    setSelectedAccountId(accountId);
    try {
      const response = await UserRepository.getUsers({
        queryParams: {
          pagination: { page: 1, limit: 300 },
          query: {
            search: {},
            filter: { googleAdsAccountId: accountId },
            sort: {},
          },
        },
      });
      const loadedUsers = response.data?.items || [];
      setSelectedUserIds(
        loadedUsers.filter((user) => user.googleAdsAccountId === accountId).map((user) => user.id),
      );
    } catch (error: any) {
      toast.error(error?.message || "Failed to load users");
    }
  };

  const toggleUsersForAccount = (accountId: string) => {
    if (selectedAccountId === accountId) {
      setSelectedAccountId(undefined);
      return;
    }
    void loadUsersForAccount(accountId);
  };

  const toggleUser = (userId: string) => {
    setSelectedUserIds((current) => current.includes(userId) ? current.filter((id) => id !== userId) : [...current, userId]);
  };

  const toggleVisibleUsers = () => {
    const availableUsers = users.filter((user) => !user.googleAdsAccountId || user.googleAdsAccountId === selectedAccountId);
    const visibleIds = availableUsers.map((user) => user.id);
    const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedUserIds.includes(id));
    setSelectedUserIds((current) => allSelected ? current.filter((id) => !visibleIds.includes(id)) : [...new Set([...current, ...visibleIds])]);
  };

  const isUserAvailableForSelectedAccount = (user: IUserData) =>
    !user.googleAdsAccountId || user.googleAdsAccountId === selectedAccountId;

  const saveUserAssignments = async () => {
    if (!selectedAccountId) return;
    setIsSavingUsers(true);
    try {
      await UserRepository.assignGoogleAdsAccountUsers({ userIds: selectedUserIds, googleAdsAccountId: selectedAccountId });
      toast.success("Google Ads account users updated");
      await loadUsersForAccount(selectedAccountId);
      await refreshUsers();
    } catch (error: any) {
      toast.error(error?.message || "Failed to update user assignments");
    } finally {
      setIsSavingUsers(false);
    }
  };

  const fetchGoogleAdsAccounts = useCallback(async () => {
    try {
      await getGoogleAdsAccounts();
    } catch (error: any) {
      toast.error(error?.message || "Failed to load Google Ads accounts");
    }
  }, [getGoogleAdsAccounts]);

  useEffect(() => {
    void fetchGoogleAdsAccounts();
  }, [fetchGoogleAdsAccounts]);

  const handleEdit = (account: TGoogleAdsAccount) => {
    setReset({
      id: account.id,
      name: account.name,
      accountId: account.accountId,
      conversionId: account.conversionId,
      loginCustomerId: account.loginCustomerId || "",
      submitLeadFormConversionActionId: account.submitLeadFormConversionActionId || account.conversionId,
      qualifiedLeadConversionActionId: account.qualifiedLeadConversionActionId || "",
      notes: account.notes || "",
    });
  };

  const handleCreateNew = () => {
    setReset({ ...DEFAULT_VALUES });
    window.requestAnimationFrame(() => {
      formMethods?.setFocus("name");
    });
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteGoogleAdsAccount(id);
      toast.success("Google Ads account deleted");
      handleCreateNew();
      await fetchGoogleAdsAccounts();
    } catch (error: any) {
      toast.error(error?.message || "Failed to delete Google Ads account");
    }
  };

  const onSubmit: SubmitHandler<TGoogleAdsAccountForm> = async (formData) => {
    const dataToSave: TGoogleAdsAccountCreate = {
      name: formData.name,
      accountId: formData.accountId,
      conversionId: formData.conversionId || formData.submitLeadFormConversionActionId,
      loginCustomerId: formData.loginCustomerId || undefined,
      submitLeadFormConversionActionId: formData.submitLeadFormConversionActionId,
      qualifiedLeadConversionActionId: formData.qualifiedLeadConversionActionId,
      notes: formData.notes || undefined,
    };

    try {
      if (formData.id) {
        await updateGoogleAdsAccount(formData.id, dataToSave);
        toast.success("Google Ads account updated");
      } else {
        await createGoogleAdsAccount(dataToSave);
        toast.success("Google Ads account created");
      }
      setReset(DEFAULT_VALUES);
      await fetchGoogleAdsAccounts();
    } catch (error: any) {
      toast.error(error?.message || "Something went wrong");
    }
  };

  const totalAccounts = googleAdsAccounts.length;

  return (
    <div className="@container mt-4 grid items-start gap-6 xl:grid-cols-[minmax(340px,380px)_minmax(0,1fr)]">
      <section className="self-start rounded-md border border-muted bg-white p-5">
        <div className="mb-5 flex items-center justify-between gap-3">
          <div>
            <Title as="h5" className="text-xl md:text-2xl">
              Configuration
            </Title>
            <p className="mt-1 text-sm text-gray-500">
              Manage the account and conversion ID mapping used by campaigns.
            </p>
          </div>
        </div>

        <Form<TGoogleAdsAccountForm>
          resetValues={reset}
          onSubmit={onSubmit}
          validationSchema={googleAdsAccountSchema}
          className="flex flex-col gap-4"
          onInitForm={setFormMethods}
        >
          {({ control, formState: { isDirty, errors } }) => (
            <>
              <Controller
                control={control}
                name="name"
                render={({ field }) => (
                  <Input
                    {...field}
                    label="Account Name"
                    placeholder="ProjectSG Main Account"
                    error={errors.name?.message}
                  />
                )}
              />

              <Controller
                control={control}
                name="accountId"
                render={({ field }) => (
                  <Input
                    {...field}
                    label="Account ID"
                    placeholder="123-456-7890"
                    error={errors.accountId?.message}
                  />
                )}
              />

              <Controller control={control} name="loginCustomerId" render={({ field }) => <Input {...field} label="Login Customer ID (MCC)" placeholder="9022579915" error={errors.loginCustomerId?.message} />} />
              <Controller control={control} name="submitLeadFormConversionActionId" render={({ field }) => <Input {...field} label="Submit Lead Form Action ID" placeholder="7692444145" error={errors.submitLeadFormConversionActionId?.message} />} />
              <Controller control={control} name="qualifiedLeadConversionActionId" render={({ field }) => <Input {...field} label="Qualified Lead Action ID" placeholder="7692389742" error={errors.qualifiedLeadConversionActionId?.message} />} />
              <Controller control={control} name="notes" render={({ field }) => <Input {...field} label="Notes (optional)" placeholder="Staging customer" error={errors.notes?.message} />} />

              <div className="flex items-center justify-end gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCreateNew}
                  disabled={!isDirty && !reset.id}
                  className="w-full @xl:w-auto"
                >
                  Clear
                </Button>
                <Button
                  type="submit"
                  isLoading={isCreating || isUpdating}
                  disabled={!isDirty}
                  className="w-full @xl:w-auto"
                >
                  {reset.id ? "Update" : "Create"}
                </Button>
              </div>
            </>
          )}
        </Form>
      </section>

      <section className="rounded-md border border-muted bg-white p-5">
        <div className="mb-5 flex items-center justify-between gap-3">
          <div>
            <Title as="h5" className="text-xl md:text-2xl">
              Configured Accounts
            </Title>
            <p className="mt-1 text-sm text-gray-500">
              {totalAccounts} account{totalAccounts === 1 ? "" : "s"} available
            </p>
          </div>
          <Button variant="outline" className="gap-2" onClick={handleCreateNew}>
            <PiPlusBold className="h-4 w-4" />
            New Account
          </Button>
        </div>

        <div className="overflow-hidden rounded-md border border-muted">
          <table className="w-full border-separate border-spacing-0">
            <thead className="bg-gray-50">
              <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                <th className="px-4 py-3">Account</th>
                <th className="px-4 py-3">Account ID</th>
                <th className="px-4 py-3">Submit / Qualified Actions</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoadingAccounts ? (
                <tr>
                  <td className="px-4 py-8 text-sm text-gray-500" colSpan={4}>
                    Loading Google Ads accounts...
                  </td>
                </tr>
              ) : googleAdsAccounts.length > 0 ? (
                googleAdsAccounts.map((account, index) => {
                  const isSelected = reset.id === account.id;

                  return (
                    <tr
                      key={account.id}
                      className={cn(
                        "border-t border-muted text-sm",
                        index === 0 && "border-t-0",
                        isSelected && "bg-gray-200/60",
                      )}
                    >
                      <td className="px-4 py-3 font-medium text-gray-900">
                        {account.name}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-600">
                        {account.accountId}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-600">
                        {account.submitLeadFormConversionActionId || account.conversionId} / {account.qualifiedLeadConversionActionId || "—"}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <Tooltip
                            size="sm"
                            content="Edit account"
                            placement="top"
                            color="invert"
                          >
                            <ActionIcon
                              size="sm"
                              variant="outline"
                              onClick={() => handleEdit(account)}
                            >
                              <PiPencilSimpleBold className="h-4 w-4" />
                            </ActionIcon>
                          </Tooltip>
                          <Button size="sm" variant="outline" onClick={() => toggleUsersForAccount(account.id)}>
                            {selectedAccountId === account.id ? "Hide Users" : "Manage Users"}
                          </Button>
                          <DeletePopover
                            title="Delete Google Ads Account"
                            description={`Delete ${account.name}? Existing users that use this account will need to be reassigned.`}
                            onDelete={() => handleDelete(account.id)}
                            isDeleting={isDeleting}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td className="px-4 py-8 text-sm text-gray-500" colSpan={4}>
                    No Google Ads accounts configured yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {selectedAccount && (
          <div className="mt-6 rounded-md border border-muted bg-gray-50 p-5">
            <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
              <div>
                <Title as="h6">Users for {selectedAccount.name}</Title>
                <p className="mt-1 text-sm text-gray-500">
                  Select users for this account. Deselecting a user removes its account assignment when saved.
                </p>
              </div>
              <Button isLoading={isSavingUsers} onClick={() => void saveUserAssignments()}>
                Save Assignments ({selectedUserIds.length})
              </Button>
            </div>
            <Input
              type="search"
              placeholder="Search by name or email..."
              value={userSearch}
              onClear={() => searchUsers("")}
              onChange={(event) => searchUsers(event.target.value)}
              prefix={<PiMagnifyingGlassBold className="h-4 w-4" />}
              rounded="lg"
              clearable
              className="mt-4 w-full"
            />
            <ControlledTable
              variant="modern"
              data={users}
              isLoading={isLoadingUsers}
              showLoadingText
              columns={[
                { title: <Checkbox checked={users.filter((user) => isUserAvailableForSelectedAccount(user)).length > 0 && users.filter((user) => isUserAvailableForSelectedAccount(user)).every((user) => selectedUserIds.includes(user.id))} onChange={toggleVisibleUsers} />, dataIndex: "id", key: "select", render: (id: string, user: IUserData) => <Checkbox checked={selectedUserIds.includes(id)} disabled={!isUserAvailableForSelectedAccount(user)} onChange={() => toggleUser(id)} /> },
                { title: <HeaderCell title="User" />, dataIndex: "firstName", key: "user", render: (_: string, user: IUserData) => <span className="font-medium">{user.firstName} {user.lastName}</span> },
                { title: <HeaderCell title="Email" />, dataIndex: "email", key: "email" },
                { title: <HeaderCell title="Current account" />, dataIndex: "googleAdsAccountId", key: "account", render: (id: string | null) => googleAdsAccounts.find((account) => account.id === id)?.name || "Unassigned" },
              ]}
              paginatorOptions={{ pageSize: userPageSize, setPageSize: changeUserPageSize, total: totalUsers, current: userPage, onChange: paginateUsers }}
              className="mt-4 rounded-md border border-muted text-sm shadow-sm"
            />
          </div>
        )}
      </section>
    </div>
  );
}

function GoogleAdsAccountsPage() {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <div className="mt-4">
      <div className="relative z-50 mb-4 flex flex-wrap items-center justify-between gap-2.5 @container">
        <Title as="h5" className="@xl:basis-auto text-xl md:text-2xl">
          Google Ads Accounts
        </Title>
      </div>

      <Tab selectedIndex={activeTab} onChange={(index) => setActiveTab(index)}>
        <Tab.List className="border-b border-gray-300">
          <Tab.ListItem>Config</Tab.ListItem>
          <Tab.ListItem>Data Manager Logs</Tab.ListItem>
        </Tab.List>
        <Tab.Panels className="mt-6">
          <Tab.Panel>
            <GoogleAdsAccountConfig />
          </Tab.Panel>
          <Tab.Panel>
            <DataManagerLogsWrapper />
          </Tab.Panel>
        </Tab.Panels>
      </Tab>
    </div>
  );
}

export default GoogleAdsAccountsPage;
